import { domAnimation, LazyMotion } from 'motion/react'
import type { ForwardRefExoticComponent, RefAttributes, SVGAttributes } from 'react'
import { useEffect, useRef } from 'react'

export type AnimatedIconHandle = {
  startAnimation: () => void
  stopAnimation: () => void
}

/**
 * The props a vendored icon accepts.
 *
 * Plain `SVGAttributes` will not do: the root is a `motion.svg`, and Motion
 * redefines the animation and drag handlers with its own signatures. Dropping
 * React's versions is what lets the rest of the svg props forward untouched.
 */
export type AnimatedIconProps = Omit<
  SVGAttributes<SVGSVGElement>,
  | 'onAnimationStart'
  | 'onAnimationEnd'
  | 'onAnimationIteration'
  | 'onDrag'
  | 'onDragStart'
  | 'onDragEnd'
  // SMIL's `values` attribute; Motion uses the same name for its MotionValues.
  | 'values'
> & { size?: number }

/** The shape every icon under `@/shared/ui/icons` exports. */
export type AnimatedIconComponent = ForwardRefExoticComponent<
  AnimatedIconProps & RefAttributes<AnimatedIconHandle>
>

type Props = {
  icon: AnimatedIconComponent
  className?: string
}

/**
 * The controls an icon is allowed to belong to. An icon never carries its own
 * hover — it sits inside a button, a dock tile or a menu row, and a glyph that
 * lights up only when the cursor lands on its own 17px is a worse affordance
 * than no animation at all.
 *
 * `[data-slot="field"]` is here because a field's leading icon is decoration
 * with `pointer-events-none`: without the field itself as the host, the search
 * glyph could never animate at all.
 */
const HOST =
  'button, a[href], [role="button"], [role="menuitem"], label, [data-slot="field"]'

/**
 * Absent `matchMedia` means an environment that is not a browser — jsdom, where
 * component tests run. Answering "reduce" there keeps this effect from throwing
 * and tearing down whatever tree happens to hold a dialog or a toast.
 */
const prefersReducedMotion = () =>
  typeof window.matchMedia !== 'function' ||
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Runs a lucide-animated icon from its enclosing control.
 *
 * The vendored icons under `icons/` expose `startAnimation` / `stopAnimation`
 * and nothing else — this is the only place that decides when either fires.
 * Focus is driven alongside hover because touch has no hover and the design
 * system forbids hover carrying what press and focus do not.
 *
 * The `contents` span is a DOM anchor with no box: it is how `closest()` finds
 * the control, and it stays invisible to the `[&_svg]:size-*` selectors that
 * `Button`, `Dock` and `TopbarPill` use to size their icons.
 */
export function AnimatedIcon({ icon: Icon, className }: Props) {
  const hostRef = useRef<HTMLSpanElement>(null)
  const handleRef = useRef<AnimatedIconHandle>(null)

  useEffect(() => {
    const anchor = hostRef.current
    if (anchor === null || prefersReducedMotion()) return

    const host = anchor.closest(HOST) ?? anchor.parentElement
    if (host === null) return

    const start = () => handleRef.current?.startAnimation()
    const stop = () => handleRef.current?.stopAnimation()

    // `pointerenter` and not `mouseenter`: a tap fires it once and then leaves,
    // which plays the animation instead of latching it on.
    host.addEventListener('pointerenter', start)
    host.addEventListener('pointerleave', stop)
    host.addEventListener('focusin', start)
    host.addEventListener('focusout', stop)

    return () => {
      host.removeEventListener('pointerenter', start)
      host.removeEventListener('pointerleave', stop)
      host.removeEventListener('focusin', start)
      host.removeEventListener('focusout', stop)
    }
  }, [])

  return (
    // Decoration, always: every control that holds one of these carries its own
    // label, and a second name read out of the glyph would only duplicate it.
    <span ref={hostRef} aria-hidden className="contents">
      {/*
       * Every icon in the app renders through here, which makes this the one
       * place Motion can be bounded.
       *
       * A `motion.*` component carries every feature the library has, and
       * importing one anywhere puts all of them in the chunk that holds it.
       * These icons sit in the dock and the topbar pill, mounted on every
       * route, so the whole of Motion was in the eager download — second in
       * weight only to the design system's own primitives. The icons now use
       * `m` instead, which is the same component with nothing attached, and the
       * features are handed to them here: 369.4 kB → 356.3 kB gzip of first
       * load.
       *
       * `strict` is what keeps it that way — a `motion.*` component anywhere
       * under this provider throws instead of quietly pulling the library back.
       *
       * The features are passed directly rather than through a dynamic import.
       * That was tried and measured 0.9 kB *worse*: `LazyMotion` and
       * `useAnimation` come from `motion/react` regardless, so the module is
       * already in the eager graph and importing it again only adds a chunk
       * boundary.
       */}
      <LazyMotion features={domAnimation} strict>
        <Icon ref={handleRef} className={className} />
      </LazyMotion>
    </span>
  )
}
